import { Link, data, useLoaderData } from 'react-router'
import { FaqAccordion } from '#app/components/brand/faq.tsx'
import {
	Eyebrow,
	Heading,
	SectionKicker,
} from '#app/components/brand/section.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import {
	getActiveProducts,
	getPurchasablePlans,
	productMetadata,
} from '#app/utils/checkout/products.server.ts'
import { formatPrice } from '#app/utils/currency.ts'
import { buildMeta, faqPageJsonLd } from '#app/utils/seo.ts'
import { type Route } from './+types/pricing.ts'
import { Footer } from './components/footer.tsx'
import Header from './components/header.tsx'

export const meta: Route.MetaFunction = () => [
	...buildMeta({
		title: 'Pricing | Competitor Backlinks',
		description:
			'We get you the links your competitors already have. Done-for-you link building at $299/month, or find your gap free first. Reports from $9.',
		path: '/pricing',
		image: '/img/og/pricing.png',
	}),
	{ 'script:ld+json': faqPageJsonLd(PRICING_FAQS) },
]

// How the service actually runs, month by month. This replaced a four-rung
// "free -> report -> pack -> placements" ladder in Aug 2026: that ladder sold
// the analysis and left the customer holding the work, which is the offer
// roadmap v3.0 retired. What we sell is the outcome. Rendered on the dark band
// as the page's tonal break. Icons are sprite ids confirmed present.
const ladder: { icon: IconName; step: string; title: string; text: string }[] =
	[
		{
			icon: 'magnifying-glass',
			step: 'Day 1',
			title: 'We find the gap',
			text: 'We pull every site linking to your competitors and subtract the ones already linking to you.',
		},
		{
			icon: 'link-2',
			step: 'Month 1',
			title: 'We start where you actually are',
			text: 'Thin profile? We build the listings you are missing first. Already covered? We skip straight to your competitors’ links.',
		},
		{
			icon: 'ticket-plus',
			step: 'Every month',
			title: 'We work the gap',
			text: 'At least two placements a month. The ones on their list we can actually get, and close matches in the same niche where we cannot.',
		},
		{
			icon: 'clipboard-list',
			step: 'Every month',
			title: 'You see what landed',
			text: 'A short report naming each live link and its URL. No dashboards to learn, nothing to chase.',
		},
	]

// Real pricing questions. Kept in one array so the visible copy and the FAQPage
// JSON-LD in the meta above can never drift.
const PRICING_FAQS: ReadonlyArray<{ q: string; a: string }> = [
	{
		q: 'What do I get for $299 a month?',
		a: 'At least two link placements a month from your competitor gap, on sites that already link to the people you compete with, plus a short report naming each live link and its URL. Where we start depends on your site: if you are missing the basic profile, directory and industry listings we build those first, and if you already have them we go straight to the competitor links.',
	},
	{
		q: 'My site is established. Is the foundation month wasted on me?',
		a: 'We would not do a foundation month for you. We run your gap before anything else, and if your profile already covers the basics there is nothing to build, so month one is competitor placements like every other month. The foundation work exists for newer or thinner sites where skipping it would make the later links work harder than they need to.',
	},
	{
		q: 'Can I cancel?',
		a: 'Any time, and you keep every link we built. There is no contract and no cancellation fee. The links are on other people’s sites pointing at yours, so they do not switch off when you stop paying.',
	},
	{
		q: 'Do I have to subscribe?',
		a: 'No. The free checker is free forever with no account, and the one-off reports and foundation packs below are still single payments. The monthly service exists for people who would rather we did the work than handed them a list.',
	},
	{
		q: 'How many links do I actually get?',
		a: 'At least two placements a month, and often more. The number moves because the cost of a placement does: some months your gap is full of sites we can get cheaply and you get five or six, other months the good ones are expensive and you get the two we promised. We would rather beat a number you can count on than advertise one we cannot always hit.',
	},
	{
		q: 'Will you get me the exact sites linking to my competitor?',
		a: 'Where those sites accept placements, yes, and that is always where we start. But plenty of them will not: a link a competitor earned from a news story or a personal blog post is not for sale at any price. When a site on your gap is unreachable we go after the closest equivalent, a site in the same niche and the same authority band, so the profile you end up with looks like theirs even when the individual URLs differ. Anyone promising you every link on that list has not checked which of them are actually buyable.',
	},
	{
		q: 'Is this only for local businesses?',
		a: 'No. Any foundation work is profile, directory and industry listings that any site benefits from, and local citations only get added when you serve a specific area. The rest of the time we work from your actual competitor gap, so what we chase is whatever is working in your market, local or not.',
	},
	{
		q: 'What is actually in a report?',
		a: 'A full, scored competitor backlink analysis: your prioritized backlink gap, the specific referring domains worth chasing ranked by how realistically you can win them, the keyword and top-page gaps, and a CSV export of everything.',
	},
	{
		q: 'What is the difference between a report and a foundation pack?',
		a: 'A report is intelligence: it tells you which links to go earn. A foundation pack is done-for-you work: our team builds and submits the baseline directory, social, and citation links every site should have. Many people buy a report first, then a pack.',
	},
	{
		q: 'Can I try it before I pay?',
		a: "Yes. The free backlink checker shows a competitor's referring-domain map and your gap with no account and no card. You only pay when you want the scored report or the done-for-you links.",
	},
	{
		q: 'Do my reports expire?',
		a: 'No. A report you buy is yours to keep, and report credits in a pack do not expire. Run them whenever you are ready.',
	},
	{
		q: 'Are the foundation-pack links safe for SEO?',
		a: 'Yes. Foundation packs are legitimate directory, social-profile, and local-citation links built by hand. No PBNs, no black-hat tactics, nothing that puts your site at risk.',
	},
]

export async function loader({}: Route.LoaderArgs) {
	// Both catalogs come from the database, so pricing changes ship as data.
	const [plans, productRows] = await Promise.all([
		// Report ladder (the credit packs, productized). One report = one full,
		// scored competitor backlink analysis. Unsynced tiers are excluded — a
		// price we can't charge must never be advertised.
		getPurchasablePlans(),
		// One-time, done-for-you products (foundation packs).
		getActiveProducts(),
	])

	// Flatten the per-product metadata here so the component stays presentational.
	const products = productRows.map((product) => ({
		sku: product.sku,
		name: product.name,
		description: product.description,
		price: product.price,
		highlights: productMetadata(product).highlights,
	}))

	return data({ plans, products })
}

export default function PricingPage() {
	const { plans, products } = useLoaderData<typeof loader>()

	return (
		<div className="bg-canvas text-ink min-h-screen font-sans">
			<Header />

			<main className="mx-auto max-w-[1080px] px-8 pt-16 pb-24">
				{/* Hero */}
				<div className="text-center">
					<Eyebrow className="mb-3.5">Done-for-you link building</Eyebrow>
					<Heading className="mb-3 text-[clamp(32px,5vw,52px)] tracking-[-0.02em]">
						We get you the links your competitors already have.
					</Heading>
					<p className="mx-auto max-w-[600px] text-lg text-pretty text-[#57565E]">
						We find every site linking to them and not to you, then go and get
						those links. $299 a month, cancel any time. Or find your gap for
						free first and decide later.
					</p>
					<div className="mt-7 flex flex-wrap items-center justify-center gap-3">
						<Button asChild size="lg">
							<Link to="/get-started">
								Get your gap fixed
								<Icon name="arrow-right" className="size-4" />
							</Link>
						</Button>
						<Button asChild size="lg" variant="outline">
							<Link to="/free-backlink-checker">See your gap free</Link>
						</Button>
					</div>
				</div>

				{/* Value ladder — the tonal break, and the map of what follows */}
				<section className="pt-14">
					<div className="bg-ink relative overflow-hidden rounded-[28px] px-6 py-10 text-white sm:px-10 sm:py-12">
						<div className="bg-brand/25 pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl" />
						<div className="relative">
							<SectionKicker tone="brand-light">How it works</SectionKicker>
							<h2 className="font-display mt-4 max-w-[560px] text-[clamp(22px,3vw,30px)] leading-[1.12] font-semibold tracking-[-0.02em]">
								You name a competitor. We go and take their links.
							</h2>
							<ol className="mt-9 grid gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
								{ladder.map((item, i) => (
									<li key={item.title} className="relative bg-[#1B1B20] p-5">
										<div className="flex items-center justify-between">
											<span className="bg-brand/20 text-brand-light flex size-9 items-center justify-center rounded-xl">
												<Icon name={item.icon} className="size-[18px]" />
											</span>
											<span className="font-mono text-[11px] tracking-[0.14em] text-white/45 uppercase">
												{item.step}
											</span>
										</div>
										<div className="font-display mt-4 flex items-center gap-2 text-[15.5px] font-semibold">
											<span className="text-white/30 tabular-nums">
												0{i + 1}
											</span>
											{item.title}
										</div>
										<p className="mt-1.5 text-[13px] leading-relaxed text-white/60">
											{item.text}
										</p>
									</li>
								))}
							</ol>
						</div>
					</div>
				</section>

				{/* The offer. Deliberately the only thing on its row and the only
				    filled CTA above the fold of this section: everything below it is
				    secondary. Price is hardcoded because this is a sales page, not a
				    catalog row — there is no recurring SKU in Stripe yet and the first
				    customers are invoiced by hand (roadmap v3.0). When a recurring
				    Price exists, read it through getPurchasablePlans like the rest. */}
				<section className="pt-[76px]">
					<div className="border-hairline mx-auto max-w-[720px] rounded-[28px] border bg-white p-8 sm:p-10">
						<div className="flex flex-wrap items-baseline justify-between gap-3">
							<div>
								<Eyebrow className="mb-2">The service</Eyebrow>
								<h2 className="font-display text-[clamp(24px,3.4vw,32px)] leading-[1.1] font-semibold tracking-[-0.02em]">
									Done-for-you link building
								</h2>
							</div>
							<div className="text-right">
								<div className="font-display text-[34px] leading-none font-semibold">
									$299
								</div>
								<div className="mt-1 text-[13px] text-[#57565E]">
									per month, cancel any time
								</div>
							</div>
						</div>

						<p className="mt-5 max-w-[560px] text-[17px] text-pretty text-[#57565E]">
							You do nothing. We run the gap against your competitors and start
							wherever you actually are, then keep placing links from the sites
							already pointing at them.
						</p>

						<ul className="mt-7 grid gap-x-6 gap-y-3 sm:grid-cols-2">
							{[
								'Missing the basics? We build those listings first',
								'Already established? We go straight to placements',
								'At least two placements a month, more when we find cheaper wins',
								'Their reachable links first, close matches in the same niche after',
								'Every link named and linked in a monthly report',
								'Fresh gap analysis every month, included',
								'No dashboards to learn, no work on your side',
								'Keep every link if you cancel',
							].map((line) => (
								<li key={line} className="flex gap-2.5 text-[15px]">
									<Icon
										name="check"
										className="text-brand mt-[3px] size-4 shrink-0"
									/>
									<span className="text-pretty">{line}</span>
								</li>
							))}
						</ul>

						<div className="mt-8 flex flex-wrap items-center gap-3">
							<Button asChild size="lg">
								<Link to="/get-started">
									Get your gap fixed
									<Icon name="arrow-right" className="size-4" />
								</Link>
							</Button>
							<span className="text-[13px] text-[#57565E]">
								Tell us your site and a competitor. We reply with what we would
								go after first.
							</span>
						</div>
					</div>
				</section>

				{/* One-time alternatives, deliberately compact.
				    This used to be three report cards plus two pack cards plus a
				    "coming soon" panel — five priced CTAs competing with the service
				    on the page paid traffic lands on. A single path converts; a menu
				    does not. They stay purchasable because they still monetize organic
				    traffic and the report page sells them at peak intent, they just
				    stop competing here.

				    `#foundation-packs` is a real deep-link target and must survive:
				    /guides/should-you-buy-backlinks and
				    /guides/backlink-building-services-explained both send readers
				    straight to it. */}
				<section id="foundation-packs" className="scroll-mt-24 pt-[84px]">
					<div className="border-hairline mx-auto max-w-[720px] rounded-[24px] border p-7 sm:p-8">
						<Eyebrow className="mb-3">Rather buy once?</Eyebrow>
						<h2 className="font-display text-[clamp(20px,2.6vw,26px)] leading-[1.15] font-semibold tracking-[-0.02em]">
							You can still just buy the data, or the foundation on its own.
						</h2>
						<p className="mt-3 max-w-[520px] text-[15px] text-pretty text-[#57565E]">
							Same work, bought a piece at a time instead of monthly. No
							subscription on any of these.
						</p>

						<ul className="mt-6 divide-y divide-[color:var(--border)]">
							{plans.slice(0, 1).map((plan) => (
								<li
									key={plan.id}
									className="flex flex-wrap items-center justify-between gap-3 py-3.5"
								>
									<span className="text-[15px]">
										<span className="font-medium">Gap reports</span>{' '}
										<span className="text-[#57565E]">
											from {formatPrice(plan.price)}, scored and ranked, with
											CSV
										</span>
									</span>
									<Link
										to={`/checkout?plan=${plan.stripePriceId}`}
										className="text-brand text-[15px] font-medium underline underline-offset-2"
									>
										Buy a report
									</Link>
								</li>
							))}
							{products.map((product) => (
								<li
									key={product.sku}
									className="flex flex-wrap items-center justify-between gap-3 py-3.5"
								>
									<span className="text-[15px]">
										<span className="font-medium">{product.name}</span>{' '}
										<span className="text-[#57565E]">
											{formatPrice(product.price)}, {product.description}
										</span>
									</span>
									<Link
										to={`/checkout?sku=${product.sku}`}
										className="text-brand text-[15px] font-medium underline underline-offset-2"
									>
										Order
									</Link>
								</li>
							))}
						</ul>
					</div>
				</section>

				{/* FAQ */}
				<section
					className="grid gap-10 pt-[84px] lg:grid-cols-[0.62fr_1.38fr] lg:gap-14"
					aria-label="Pricing questions"
				>
					<div>
						<SectionKicker>Questions</SectionKicker>
						<Heading className="mt-4 text-[clamp(24px,3.4vw,32px)]">
							No contracts, no surprises
						</Heading>
						<p className="mt-4 max-w-[320px] text-[15px] leading-relaxed text-[#57565E]">
							Still deciding? Start with the{' '}
							<Link
								to="/free-backlink-checker"
								className="text-brand font-medium underline underline-offset-2"
							>
								free checker
							</Link>{' '}
							and upgrade only if it earns it.
						</p>
					</div>
					<FaqAccordion faqs={PRICING_FAQS} />
				</section>

				{/* Trust line */}
				<div className="mt-[84px] flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-[#8A867D]">
					<span className="flex items-center gap-2">
						<Icon name="shield-check" className="size-4" />
						Secure payment
					</span>
					<span className="flex items-center gap-2">
						<Icon name="scan-face" className="size-4" />
						Cancel any time
					</span>
					<span className="flex items-center gap-2">
						<Icon name="mail" className="size-4" />
						Email support
					</span>
				</div>
			</main>

			<Footer />
		</div>
	)
}
